<template>
  <div v-if="!isError" class="image-container">
    <!-- Checkbox Icon -->
    <div
      v-if="isUploaded && selectable"
      class="checkbox-icon"
      :class="{ 'checkbox-selected': isSelected }"
      @click.stop="toggleSelection">
      <IconCircleCheck v-if="isSelected" :size="20" />
      <IconCircle v-else :size="20" />
    </div>

    <!-- Image Container -->
    <div
      class="image-wrapper"
      :class="{
        selectable: selectable,
        selected: isSelected && selectable,
        unselected: selectable && !isSelected,
      }"
      @click="handleContainerClick">
      <img :src="imageUrl" alt="Order image" class="image" :class="{ uploading: !isUploaded }" />

      <!-- Remove Button -->
      <IconTrash :size="24" class="remove-btn" role="button" @click.stop="$emit('remove')" />

      <!-- Loading Spinner -->
      <div v-if="!isUploaded" class="loading-spinner">
        <div class="spinner-border" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import type { UploadableFile } from './utils';
import { IconTrash, IconCircle, IconCircleCheck } from '@tabler/icons-vue';

interface Props {
  uploadable: UploadableFile;
  selectable?: boolean;
  allowSelection?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  toggleSelect: [selected: boolean];
  remove: [];
}>();

// Computed properties
const isUploaded = computed(() => props.uploadable.percentage === 100);
const isSelected = computed(() => props.uploadable.file?.selected || false);
const imageUrl = computed(() => (isUploaded.value ? props.uploadable.file?.proxy?.url_preview : props.uploadable.url));
const isError = computed(() => props.uploadable.status === false);
const selectable = computed(() => props.selectable && isUploaded.value);

// Event handlers
const toggleSelection = () => {
  emit('toggleSelect', !isSelected.value);
};

const handleContainerClick = () => {
  if (selectable.value) {
    toggleSelection();
  }
};
</script>
<style scoped>
.image-container {
  position: relative;
  height: 100%;
  width: 100%;
}

.checkbox-icon {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 20;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 3px;
  color: rgba(0, 0, 0, 0.4);
}

.checkbox-icon.checkbox-selected {
  color: var(--tblr-primary);
}

.image-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image:
    linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(45deg, #e0e0e0 25%, #fff 25%);
  background-size: 14px 14px;
  background-position:
    0 0,
    0 0,
    -7px -7px,
    7px 7px;
  border: 1px solid #d3d3d3;
  border-radius: 5px;
  padding: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.image-wrapper.selectable {
  cursor: pointer;
}

.image-wrapper.selected {
  border: 2px solid var(--tblr-primary);
  opacity: 1;
}

.image-wrapper.unselected {
  opacity: 0.6;
}

.image {
  max-height: 120px;
  max-width: 100%;
  object-fit: contain;
  transition: opacity 0.2s ease;
}

.image.uploading {
  opacity: 0.6;
}

.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 15;
  color: #6c757d;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 4px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-wrapper:hover .remove-btn {
  opacity: 1;
}

.remove-btn:hover {
  background-color: white;
  color: #dc3545;
  transform: scale(1.1);
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>
